/**
 * 見積書の紙（PDF）を画面の中で見る・印刷する・保存する（R13、2026-09-15 射場さん）。
 *   ブラウザの PDF 画面をそのまま出すと、タブに API の URL が出て、印刷のボタンも分かりにくい。
 *   ここでは PDF を pdf.js で描いて紙として並べ、上に「印刷する」「PDF 保存」「見積書に戻る」を置く。
 *   タブの名前は「見積書：工事名」。印刷はこの画面の紙だけを出す（画面のメニューや帯は出ない）。
 *   印刷は「1ページ＝画像1枚」を body 直下の #printContainer に並べて刷る（styles.css の @media print、R14）。
 *   Safari は @page の余白ゼロを無視して自分の余白とヘッダ・フッタを付けるので、紙の大きさを mm で決め打ちすると白紙が挟まる。
 *   お客様に送る PDF は「PDF 保存」（サーバーが作った本物の PDF。文字が文字のまま）を使う
 *   書式（#78、044、2026-09-24 射場さん）：見積書・請求書は「書式」（プリセット 縦3・横2）を選べる。9/23 の「縦向き｜横向き」はここに吸収。
 *   下書きは 顧客ごとの指定 → 会社の既定 → 標準 の順で決まり、ここで選ぶと下書きに残る（PUT /…/paper）。出した紙は固めてあるので変えられない。領収書は縦だけ
 *   送付状（#223、docs/89 §5.1）：出した帳票だけ・PC だけ、紙の右に「送付状」の枠（components/CoverLetterPanel.tsx）。オンなら送付状の見本のページを紙の前に並べ、
 *   「印刷」「PDF 保存」は POST /deliveries で送付状＋この紙の PDF 1本を作って固めてから（送付の記録）。オフなら今のまま。帳票の PDF の取り方は変えない
 *   2回目（docs/89 §6）：枠の「同じ送付先の書類を追加」で書類を選ぶと、この帳票と選んだ書類を持って束ねの画面（/cover-letters/new）へ（枠で直した中身も引き継ぐ）
 *   メール送付（#225、docs/91 §3.1）：右の枠のいちばん上に「送信方法［印刷・FAX｜メール］」。メールにすると枠がメールの欄（components/MailPanel.tsx）と送付状（既定オフ）に替わり、
 *   左はメールの見本と送る PDF（送付状・この帳票・同じ送付先の書類）、見出しの主ボタンは「送信」（短い確認 → POST /mail-deliveries）。同じ送付先の書類はこの画面の中で足す（束ねの画面へ移らない）
 */
import { ACCEPTANCE_MSG, CONFLICT_MSG, COVER_LETTER_MSG, DELIVERY_NOTE_MSG, MAIL_MSG, MSG, PAPER_MSG, coverItemKey, type DeliveryItemRef, type DeliveryNote, type Estimate, type Invoice, type PaperPresetList } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError, api, fetchFile, messageOf } from "../api.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { docShort } from "../components/AreaIcon.jsx";
import { CoverBundlePicker, useItemPages } from "../components/CoverBundle.jsx";
import { CoverLetterPanel, contentOf, useCoverLetter, type CoverLetter } from "../components/CoverLetterPanel.jsx";
import { MailPanel, MailPreview, MailSendDialog, useMailDelivery } from "../components/MailPanel.jsx";
import { Segmented } from "../components/Segmented.jsx";
import type { CoverBundleState } from "./CoverLetterNew.jsx";
import { downloadBlob, portraitForPrint, renderPdf, type PageImg } from "../lib/pdfPages.js";
import { usePdfTab } from "../lib/pdfOpen.js";
import { useNarrow } from "../lib/useNarrow.js";
import { currentView } from "../lib/view.js";
import { PaperThumb } from "../components/PaperThumb.jsx";
import { ErrorText, Help, Switch, useToast } from "../components/ui.jsx";
import { useMe } from "../auth.js";
import { uses } from "../core/uses.js";
import { t } from "../labels.js";

export type { PageImg } from "../lib/pdfPages.js";
export { renderPdf } from "../lib/pdfPages.js";
const COVER_MAKING = "送付状を作成中…";
/** 横の紙は縦と同じ縮尺で並べる（縦は styles.css の .paperpage で 820px ＝ 210mm → 横 297mm ≒ 1160px） */
const LAND_PAGE = { width: "min(100%, 1160px)" } as const;

/** kind：見積書（/estimates/:id/paper）か 請求書（/invoices/:id/paper）か 領収書（/receipts/:id/paper）。紙の作りは同じで、名前と戻り先が違う */
/** プルダウンの候補に付ける由来（この顧客の指定／会社の既定） */
function sourceTag(paper: { source: string; ref: string }, ref: string): string {
  if (ref !== paper.ref) return "";
  return paper.source === "partner" ? "（この顧客の指定）" : paper.source === "tenant" ? "（会社の既定）" : "";
}

export function PaperPage({ kind = "quote" }: { kind?: "quote" | "invoice" | "receipt" | "delivery_note" | "acceptance" }) {
  const { id = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const form = sp.get("form") === "flat" ? "flat" : "sheets";
  const isInv = kind === "invoice";
  const isRcpt = kind === "receipt";
  // {deliveryNote}（#266、docs/96）：書式は選ばない（標準の紙だけ）・紙の形は1つ・名前は会社の言葉
  const isDnOnly = kind === "delivery_note";
  // {acceptance}（#266 1b）も同じ扱い（書式なし・紙の形は1つ）
  const isAc = kind === "acceptance";
  const isDn = isDnOnly || isAc;
  /** 書式を選ぶ帳票（{deliveryNote}・{acceptance}は書式を持たない＝paper が無いので選ぶ欄は出ない） */
  const paperKind = kind === "delivery_note" || kind === "acceptance" ? null : kind;
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  /** 紙の形を URL に書く（ほかの値は残す。既定の値は書かない） */
  const setParam = (k: "form", v: string | null) => {
    const next = new URLSearchParams(sp);
    if (v == null) next.delete(k);
    else next.set(k, v);
    setSp(next, { replace: true });
  };
  const base = isAc ? "acceptances" : isDn ? "delivery-notes" : isRcpt ? "receipts" : isInv ? "invoices" : "estimates";
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ その見積書・請求書・領収書・{deliveryNote}
  const qKey = [isAc ? "acceptance" : isDn ? "delivery-note" : isRcpt ? "receipt" : isInv ? "invoice" : "estimate", id];
  const est = useQuery({ queryKey: qKey, queryFn: () => api<Estimate | Invoice | DeliveryNote>("GET", `/${base}/${id}`), retry: false });
  const label = isAc
    ? (est.data as DeliveryNote | undefined)?.names.acceptance || t("{acceptance}")
    : isDn
      ? ((est.data as DeliveryNote | undefined)?.names.deliveryNote ?? "納品書")
      : isRcpt
        ? "領収書"
        : isInv
          ? "請求書"
          : "見積書";
  // 組合せ PDF（#266 1b）：請求書＋{deliveryNote}（＋{acceptance}）。載った{deliveryNote}があるときだけ・{acceptance}はスイッチがオンで発行済があるときだけ
  const bundlePdf = usePdfTab();
  const fromNotes = isInv ? (est.data as Invoice | undefined)?.fromDeliveryNotes : null;
  const accSwitch = isInv ? (est.data as Invoice | undefined)?.acceptanceSwitch : null;
  // スイッチ「{acceptance}」の3段目＝この請求書（使う機能「{acceptance}」の会社だけ。null＝{customer}に従う）
  const acUses = uses(useMe().data, "acceptance");
  const [accBusy, setAccBusy] = useState(false);
  // 押した瞬間に切り替える（答えを待つ間も つまみ は押した側。断られたら戻す）
  const [accOn, setAccOn] = useState<boolean | null>(null);
  const accChecked = accOn ?? !!accSwitch?.on;
  const setAccSwitch = async (on: boolean) => {
    setAccBusy(true);
    setAccOn(on);
    try {
      await api("PUT", `/invoices/${id}/acceptance`, { attach: on });
      await qc.invalidateQueries({ queryKey: qKey });
      setAccOn(null);
    } catch (e) {
      setAccOn(!on);
      toast(messageOf(e), undefined, "error");
    } finally {
      setAccBusy(false);
    }
  };
  const short = est.data ? docShort(label, est.data.displayNo ?? est.data.documentNo) : null;
  const back = useBackNav({ to: `/${base}/${id}`, label: short ?? label });
  usePageName(label, short);
  // 書式（#78、044・046）：プリセットの一覧と、この紙の書式。領収書はプリセットが標準だけ（＋自分の書式。046）
  const presets = useQuery({ queryKey: ["paper-presets"], queryFn: () => api<PaperPresetList>("GET", "/paper/presets") });
  const paper = est.data?.paper;
  const land = paper?.orient === "landscape";
  /**
   * 書式を選ぶ（#202：版を付けて送る）。ほかで変わっていたら（409）最新を読み、選んだ書式をもう一度送る
   * （書式は1つの値＝この画面で選んだものを最新に重ねるだけ。通知「他の利用者が変更しました。再読込して、この画面の修正を保存しました」）
   */
  const pickPaper = useMutation({
    mutationFn: async (ref: string) => {
      const send = (version: string | undefined) => api<Estimate | Invoice>("PUT", `/${base}/${id}/paper`, { ref, ...(version ? { version } : {}) });
      try {
        return { d: await send(est.data?.updatedAt), merged: false };
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 409 && e.code?.endsWith("_STALE"))) throw e;
        const latest = await api<Estimate | Invoice>("GET", `/${base}/${id}`);
        return { d: await send(latest.updatedAt), merged: true };
      }
    },
    onSuccess: ({ d, merged }) => {
      qc.setQueryData(qKey, d);
      if (merged) toast(CONFLICT_MSG.autoMerged);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  const [pages, setPages] = useState<PageImg[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  // 取ってきた PDF（「PDF 保存」はこれを保存する＝API にもう一度作らせない。docs/72 §10）
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  // 印刷用の見た目（styles.css の body.paper-mode）。この画面にいる間だけ。⌘P でも同じ紙だけが刷られる
  useEffect(() => {
    document.body.classList.add("paper-mode");
    return () => {
      document.body.classList.remove("paper-mode");
    };
  }, []);

  // 領収書は常に1枚に続けて（紙の形の選択なし）
  const hasBreakdown = !isRcpt && !isDn && !!est.data?.sheets.some((s) => s.type === "breakdown" && s.inUse !== false); // #199：使う内訳があるときだけ
  const pdfQs = new URLSearchParams();
  if (hasBreakdown && form === "flat") pdfQs.set("form", "flat");
  const pdfQ = pdfQs.toString(); // URLSearchParams.size は iOS 16 の Safari に無いので使わない
  // 書式は下書きに残してあるので URL には書かない。選び直すと est.data が変わって描き直す（キャッシュ避けに ref を付ける）
  const pdfUrl = `/api/v1/${base}/${id}/pdf${pdfQ ? `?${pdfQ}` : ""}${paper && !paper.frozen ? `${pdfQ ? "&" : "?"}paper=${encodeURIComponent(paper.ref)}` : ""}`;
  const subject = est.data?.revision.subject || label;
  // 訂正版の下書き（#27）も「下書き」の紙。番号は枝番つき（Q-0012-2）
  const draft = est.data?.status === "draft" || !!est.data?.revising;
  // 送付状（#223）：出した帳票・PC だけ（下書きの紙には付けない＝docs/89 §15 の2・スマホは §15 の14）
  const narrow = useNarrow();
  const pc = !narrow && currentView() !== "phone";
  const coverTarget = est.data && !draft && pc ? { items: [{ kind, id, ...((kind === "quote" || kind === "invoice") && hasBreakdown && form === "flat" ? { form: "flat" as const } : {}) }] } : null;
  // 送信方法（#225）：印刷・FAX｜メール。帳票ごとに選ぶ（端末に覚えない）。
  // 初めは{customer}の送信設定の「送信方法」（#303 B2。送付状の初めの値と一緒に届く）。届くまでは選べない形で待つ（押す所をずらさない＝#179）
  const [viaSel, setVia] = useState<"print" | "mail" | null>(null);
  const cover = useCoverLetter(coverTarget);
  const viaKnown = viaSel !== null || !!cover.draft.data || cover.draft.isError;
  const via: "print" | "mail" = viaSel ?? cover.draft.data?.sendMethod ?? "print";
  const mailMode = via === "mail" && !!coverTarget;
  const coverOn = !!coverTarget && cover.on && !mailMode;
  const bundleTo = useBundleTo(cover);
  // メール：同じ送付先の書類はこの画面の中で足す（上へ・下へ・削除）。送付状は既定オフ（窓付封筒の紙・折り目の印なし）
  const [mailExtra, setMailExtra] = useState<DeliveryItemRef[]>([]);
  const [mailPicking, setMailPicking] = useState(false);
  const mailTarget = mailMode && coverTarget ? { items: [...coverTarget.items, ...mailExtra] } : null;
  const mail = useMailDelivery(mailTarget);
  const mailCover = useCoverLetter(mailTarget, { mail: true });
  const mailCoverOn = mailMode && mailCover.on;
  const extraPages = useItemPages(mailMode ? mailExtra : []);
  const [mailConfirm, setMailConfirm] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const [mailError, setMailError] = useState<string | undefined>();
  const moveExtra = (i: number, dir: -1 | 1) =>
    setMailExtra((xs) => {
      const j = i + dir;
      if (j < 0 || j >= xs.length) return xs;
      const next = xs.slice();
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  const sendMail = async () => {
    setMailBusy(true);
    setMailError(undefined);
    try {
      await mail.send(mailCoverOn && mailCover.form ? contentOf(mailCover.form) : null);
      setMailConfirm(false);
      toast(MAIL_MSG.sent);
    } catch (e) {
      setMailConfirm(false);
      setMailError(messageOf(e));
    } finally {
      setMailBusy(false);
      void qc.invalidateQueries({ queryKey: ["deliveries"] }); // 帳票の画面の「送付 n回」（送信失敗も記録に残る）
      void qc.invalidateQueries({ queryKey: ["mail-draft"] }); // 今日の通数
    }
  };
  const [coverBusy, setCoverBusy] = useState(false);
  /** 印刷する紙（送付状がオンのときは、固めた PDF 1本の画像。横のページは縦の紙に回す） */
  const [printPages, setPrintPages] = useState<PageImg[] | null>(null);
  useEffect(() => {
    if (!printPages) return;
    // 描き終えてから刷る（画像が DOM に入る次のフレーム）
    const r = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => cancelAnimationFrame(r);
  }, [printPages]);
  const deliverCover = async (method: "print" | "download") => {
    setCoverBusy(true);
    try {
      const { result, blob } = await cover.deliver(method);
      void qc.invalidateQueries({ queryKey: ["deliveries"] }); // 帳票の画面の「送付 n回」
      if (method === "download") downloadBlob(blob, result.fileName);
      else {
        const got: PageImg[] = [];
        await renderPdf(await blob.arrayBuffer(), (p, i) => {
          got[i - 1] = p;
        });
        setPrintPages(await portraitForPrint(got));
      }
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setCoverBusy(false);
    }
  };
  const dispNo = est.data?.displayNo ?? est.data?.documentNo;
  const fileName = est.data ? (dispNo && !draft ? `${label}_${dispNo}_${subject}.pdf` : `${label}_下書き_${subject}.pdf`) : `${label}.pdf`;

  // タブの名前：「見積書：工事名」（下書きは「見積書（下書き）：工事名」）。離れたら元に戻す
  useEffect(() => {
    if (!est.data) return;
    const prev = document.title;
    document.title = `${draft ? `${label}（下書き）` : label}：${subject}`;
    return () => {
      document.title = prev;
    };
  }, [est.data, draft, subject, label]);

  // PDF を取って描く
  useEffect(() => {
    if (!est.data) return;
    let alive = true;
    let made: string | null = null;
    setPages([]);
    setTotal(0);
    setError(undefined);
    setLoading(true);
    setBlobUrl(null);
    (async () => {
      try {
        // 断られたら API の文（混み合い＝503 PDF_BUSY・ログイン切れ）。PDF はサーバーで順番を待つことがある（docs/72 §10）
        const blob = await fetchFile(pdfUrl);
        if (!alive) return;
        made = URL.createObjectURL(blob);
        setBlobUrl(made);
        await renderPdf(await blob.arrayBuffer(), (p, i, n) => {
          if (!alive) return;
          setTotal(n);
          setPages((ps) => {
            const next = ps.slice();
            next[i - 1] = p;
            return next;
          });
        });
      } catch (e) {
        if (alive) setError(messageOf(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [est.data, pdfUrl]);

  const doPrint = () => {
    if (pages.length === 0) return;
    if (coverOn) {
      void deliverCover("print");
      return;
    }
    setPrintPages(null);
    window.print();
  };

  if (est.isError) {
    return (
      <div className="soonbox">
        <ErrorText>{messageOf(est.error)}</ErrorText>
        <p>
          {isRcpt ? <Link className="btn text" to="/invoices">請求書一覧へ</Link> : <Link className="btn text" to={isAc ? "/delivery-notes?tab=acceptance" : `/${base}`}>{isDn ? `${t(DELIVERY_NOTE_MSG.list)}一覧へ` : `${label}一覧へ`}</Link>}
        </p>
      </div>
    );
  }

  return (
    <div className="paperview">
      {/* 送付状を付けて刷るときは縦（送付状は A4 縦・横のページは画像を回してある） */}
      <style>{`@page { size: A4${land && !(coverOn && printPages) ? " landscape" : ""}; margin: 0; }`}</style>
      <BackButton back={back} />
      <div className="hrow paperbar">
        {/* 題は帳票の名前だけ。下書きは札「下書き」（題の後ろに「（下書き）」を書かない＝2026-10-21 射場さんの決め、docs/89 ★17） */}
        <h1>{label}</h1>
        {draft ? (
          <span className="chip" data-status="draft" data-testid="paper-status">
            下書き
          </span>
        ) : null}
        <span className="hint" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {est.data ? `${dispNo && !draft ? dispNo + "　" : ""}${subject}` : "読み込んでいます…"}
        </span>
        <span className="sp" />
        {fromNotes && fromNotes.notes.length && pc ? (
          <>
            {acUses && accSwitch ? (
              <span className="dh-switch" data-testid="paper-acceptance-switch-wrap">
                <Switch checked={accChecked} disabled={accBusy} onChange={(v) => void setAccSwitch(v)} label={t("{acceptance}")} testId="paper-acceptance-switch" />
                <Help label={t(ACCEPTANCE_MSG.switch)} text={t(ACCEPTANCE_MSG.switchHelp)} testId="help-paper-acceptance-switch" />
              </span>
            ) : null}
            <button type="button" className="btn" disabled={!!bundlePdf.busy} onClick={() => void bundlePdf.open(`/api/v1/invoices/${id}/bundle.pdf`)} title={t(ACCEPTANCE_MSG.bundleHelp)} data-testid="paper-bundle-notes">
              {t(ACCEPTANCE_MSG.bundleNotes)}
            </button>
            {acUses && accChecked && fromNotes.acceptances > 0 ? (
              <button type="button" className="btn" disabled={!!bundlePdf.busy} onClick={() => void bundlePdf.open(`/api/v1/invoices/${id}/bundle.pdf?with=all`)} title={t(ACCEPTANCE_MSG.bundleHelp)} data-testid="paper-bundle-all">
                {t(ACCEPTANCE_MSG.bundleAll)}
              </button>
            ) : null}
          </>
        ) : null}
        {/* 選んでいるプリセットの見本の絵（#216。PC だけ。自分の書式には無い） */}
        {paper && !paper.own ? (() => {
          const cur = presets.data?.presets.find((p) => p.key === paper.preset);
          return cur && paperKind ? <PaperThumb src={cur.thumbs[paperKind]} orient={cur.orient} name={cur.name} /> : null;
        })() : null}
        {!paper || !paperKind ? null : (
          <select
            className="pdfform papersel"
            aria-label="書式"
            data-testid="paper-select"
            value={paper.ref}
            disabled={paper.frozen || pickPaper.isPending}
            title={paper.frozen ? PAPER_MSG.issued : "書式（会社の既定と顧客ごとの指定から決まります。ここで選ぶとこの下書きに残ります）"}
            onChange={(e) => pickPaper.mutate(e.target.value)}
          >
            {(presets.data?.presets.filter((p) => p.kinds.includes(paperKind)) ?? [{ key: paper.preset, name: paper.name, desc: "", orient: paper.orient }]).map((p) => (
              <option key={p.key} value={`preset:${p.key}`}>
                {p.name}
                {sourceTag(paper, `preset:${p.key}`)}
              </option>
            ))}
            {/* 自分の書式（045）。出したあと（frozen）で消された書式も、その紙の値としては出す */}
            {(() => {
              const own = presets.data?.own[paperKind] ?? [];
              const list = paper.own && !own.some((x) => `template:${x.id}` === paper.ref) ? [...own, { id: paper.ref.slice(9), name: paper.name, orient: paper.orient }] : own;
              return list.length ? (
                <optgroup label="カスタム書式">
                  {list.map((x) => (
                    <option key={x.id} value={`template:${x.id}`}>
                      {x.name}（{x.orient === "landscape" ? "横" : "縦"}）{sourceTag(paper, `template:${x.id}`)}
                    </option>
                  ))}
                </optgroup>
              ) : null;
            })()}
          </select>
        )}
        {hasBreakdown ? (
          <select className="pdfform" aria-label="PDF の紙の形" value={form} onChange={(e) => setParam("form", e.target.value === "flat" ? "flat" : null)}>
            <option value="sheets">表紙＋内訳ごとの紙</option>
            <option value="flat">1枚に続けて</option>
          </select>
        ) : null}
        {coverOn ? (
          <button type="button" className="btn" disabled={!blobUrl || coverBusy || !cover.ready} onClick={() => void deliverCover("download")} title="送付状を1枚目にした PDF をこの端末に保存します" data-testid="paper-save">
            PDF 保存
          </button>
        ) : (
          <a className={"btn" + (blobUrl ? "" : " disabled")} href={blobUrl ?? undefined} download={fileName} aria-disabled={!blobUrl} title="PDF をこの端末に保存します" data-testid="paper-save">
            PDF 保存
          </a>
        )}
        {mailMode ? (
          <button
            type="button"
            className="btn primary"
            onClick={() => setMailConfirm(true)}
            disabled={!mail.ready || mail.problems.length > 0 || mailBusy || mail.uploading || (mailCoverOn && !mailCover.ready)}
            data-testid="mail-send"
          >
            {mailBusy ? MAIL_MSG.sending : MAIL_MSG.send}
          </button>
        ) : (
          <button type="button" className="btn primary" onClick={doPrint} disabled={loading || pages.length === 0 || !!error || coverBusy || (coverOn && !cover.ready)} title="この紙をプリンターで印刷します" data-testid="paper-print">
            {coverBusy ? COVER_MAKING : "印刷"}
          </button>
        )}
      </div>
      <ErrorText>{error}</ErrorText>
      {loading && pages.length === 0 && !error ? <p className="hint" role="status" data-testid="paper-making">{MSG.pdfMaking}</p> : null}
      <div className={coverTarget ? "paperwrap" : undefined}>
        <div className="pages" aria-label={`${label}の紙`}>
          {/* メールの見本（メールのとき。紙の前に） */}
          {mailMode ? <MailPreview m={mail} companyName={mail.draft.data?.values.company ?? ""} /> : null}
          {/* 送付状の見本（オンのとき。紙の前に並べる） */}
          {coverOn || mailCoverOn
            ? (mailMode ? mailCover : cover).pages.map((p, i) => (
                <div className="clsheet" key={`c${i}`} data-testid="cover-sheet">
                  <img src={p.url} alt={`送付状 ${i + 1}ページ目`} width={p.w} height={p.h} />
                </div>
              ))
            : null}
          {pages.map((p, i) =>
            p ? (
              <div className="paperpage" key={i} style={land ? LAND_PAGE : undefined}>
                <img src={p.url} alt={`${subject} ${i + 1}ページ目${total ? `／${total}` : ""}`} width={p.w} height={p.h} />
              </div>
            ) : null,
          )}
          {/* メールで足した同じ送付先の書類の紙（送る順） */}
          {mailMode
            ? extraPages.flatMap((q, k) =>
                (q.data ?? []).map((p, i) => (
                  <div className="paperpage" key={`x${k}-${i}`} data-testid="mail-extra-sheet">
                    <img src={p.url} alt={`追加した書類 ${k + 1} の ${i + 1}ページ目`} width={p.w} height={p.h} />
                  </div>
                )),
              )
            : null}
        </div>
        {coverTarget ? (
          <div className="sendcol" data-testid="send-column">
            <div className={"clpanel sendvia" + (viaKnown ? "" : " is-wait")} aria-busy={viaKnown ? undefined : true}>
              <span className="cllbl" id="sendvia-l">
                {MAIL_MSG.via}
              </span>
              <Segmented
                options={[
                  { value: "print" as const, label: MAIL_MSG.viaPrint, testId: "send-via-print" },
                  { value: "mail" as const, label: MAIL_MSG.viaMail, testId: "send-via-mail" },
                ]}
                value={via}
                onChange={setVia}
                labelledBy="sendvia-l"
                testId="send-via"
              />
            </div>
            {mailMode ? (
              <>
                <MailPanel m={mail} bundle={{ extra: mailExtra, onMove: moveExtra, onRemove: (i) => setMailExtra((xs) => xs.filter((_, j) => j !== i)), onAddDocs: () => setMailPicking(true) }} error={mailError} />
                <CoverLetterPanel c={mailCover} showWindow={false} testId="mail-cover-panel" />
              </>
            ) : (
              <CoverLetterPanel c={cover} onAddDocs={bundleTo.open} addDocsLabel={COVER_LETTER_MSG.addSameRecipient} />
            )}
          </div>
        ) : null}
      </div>
      {coverTarget && bundleTo.picking ? <CoverBundlePicker target={coverTarget} onClose={bundleTo.close} onPick={(refs, labels) => bundleTo.go(coverTarget.items, refs, labels)} /> : null}
      {mailTarget && mailPicking ? (
        <CoverBundlePicker
          target={mailTarget}
          onClose={() => setMailPicking(false)}
          onPick={(refs, labels) => {
            setMailPicking(false);
            setMailExtra((xs) => [...xs, ...refs.filter((r) => !xs.some((x) => coverItemKey(x) === coverItemKey(r))).map((r) => ((r.kind === "received" || r.kind === "file") && labels[coverItemKey(r)] ? { ...r, label: labels[coverItemKey(r)] } : r))]);
          }}
        />
      ) : null}
      {mailConfirm && mail.form ? <MailSendDialog m={mail} cover={mailCoverOn} busy={mailBusy} onClose={() => setMailConfirm(false)} onSend={() => void sendMail()} /> : null}
      {total > 0 ? <p className="hint" style={{ textAlign: "center" }}>{`${total}ページ`}</p> : null}
      {toastNode}
      {createPortal(
        <div id="printContainer" aria-hidden="true">
          {(coverOn && printPages ? printPages : pages).map((p, i) =>
            p ? (
              <div className="printedPage" key={i}>
                <img src={p.url} alt="" width={p.w} height={p.h} />
              </div>
            ) : null,
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * 「同じ送付先の書類を追加」（#223 の2回目、docs/89 §6）：窓で書類を選ぶと、この帳票（と見積依頼書）＋選んだ書類を持って束ねの画面へ。
 * 送付先は枠の送付先（取引先・支店）、枠で直した中身（件名・本文・手の行…）と送付状のオン／オフも持って行く（location.state）
 */
export function useBundleTo(cover: CoverLetter) {
  const nav = useNavigate();
  const [picking, setPicking] = useState(false);
  const go = (base: ReadonlyArray<DeliveryItemRef>, picked: DeliveryItemRef[], labels: Record<string, string>) => {
    setPicking(false);
    const d = cover.draft.data;
    // 帳票の紙の画面の送付先はいつも登録した送付先（未登録の送付先＝★13 は単体送付状の画面だけ）
    if (!d || !d.partner.id || !picked.length) return;
    const qs = new URLSearchParams({ partner: d.partner.id, items: [...base, ...picked].map(coverItemKey).join(",") });
    if (d.branch) qs.set("branch", d.branch.id);
    const state: CoverBundleState = { labels, ...(cover.form ? { cover: { form: cover.form, on: true } } : {}) };
    nav(`/cover-letters/new?${qs.toString()}`, { state });
  };
  return { picking, open: () => setPicking(true), close: () => setPicking(false), go };
}
